// Privacy Service
// Core service for managing user privacy settings and consent

import { supabase } from '../utils/supabase';
import { createLogger } from '../utils/logger';
import { cookieConsentManager } from '../utils/cookieConsent';
import type { 
  PrivacySettings, 
  ConsentPreferences, 
  ConsentType, 
  ConsentCheckResult,
  PrivacyServiceResponse,
  AnonymousConsent
} from '../types/privacy';
import { 
  DEFAULT_CONSENT, 
  PRIVACY_VERSION,
  PRIVACY_STORAGE_KEYS,
  PRIVACY_CACHE_CONFIG,
  createDefaultPrivacySettings,
  isValidConsent,
  isPrivacyCompliant
} from '../constants/privacy';

const logger = createLogger({ module: 'PrivacyService' });

class PrivacyService {
  private static instance: PrivacyService;
  private cache = new Map<string, { data: PrivacySettings; timestamp: number }>();
  private migrationAttempted = false;

  private constructor() {}

  public static getInstance(): PrivacyService {
    if (!PrivacyService.instance) {
      PrivacyService.instance = new PrivacyService();
    }
    return PrivacyService.instance;
  }

  // Check if user has consent for a specific type
  async checkConsent(userId: string | null, consentType: ConsentType): Promise<ConsentCheckResult> {
    try {
      // Anonymous users - check cookies first, then localStorage as fallback
      if (!userId) {
        return this.checkAnonymousConsent(consentType);
      }

      // Authenticated users - check database
      const settings = await this.getUserPrivacySettings(userId);
      
      if (!settings) {
        // No settings = privacy-first defaults (only necessary consent)
        return {
          hasConsent: consentType === 'necessary',
          consentType,
          lastUpdated: undefined
        };
      }

      return {
        hasConsent: settings.consent[consentType] === true,
        consentType,
        lastUpdated: settings.last_updated
      };
    } catch (error) {
      logger.error('Error checking consent', error);
      // Fail safe - only allow necessary consent
      return {
        hasConsent: consentType === 'necessary',
        consentType
      };
    }
  }

  // Get user's privacy settings
  async getUserPrivacySettings(userId: string): Promise<PrivacySettings | null> {
    try {
      // Check cache first
      const cached = this.cache.get(userId);
      if (cached && (Date.now() - cached.timestamp) < PRIVACY_CACHE_CONFIG.TTL) {
        return cached.data;
      }

      // Fetch from database
      const { data, error } = await supabase
        .from('user_settings')
        .select('privacy_settings')
        .eq('user_id', userId)
        .single();

      if (error) {
        logger.warn('Error fetching privacy settings', { userId, error: error.message });
        return null;
      }

      const privacySettings = data?.privacy_settings as PrivacySettings | null;
      
      // Validate and cache
      if (privacySettings && this.isValidPrivacySettings(privacySettings)) {
        this.cache.set(userId, { data: privacySettings, timestamp: Date.now() });
        return privacySettings;
      }

      return null;
    } catch (error) {
      logger.error('Error getting user privacy settings', error, { userId });
      return null;
    }
  }

  // Update user's privacy settings
  async updatePrivacySettings(
    userId: string, 
    consent: ConsentPreferences,
    auditInfo?: { ip_address?: string; user_agent?: string }
  ): Promise<PrivacyServiceResponse<PrivacySettings>> {
    try {
      // Validate consent
      if (!isValidConsent(consent) || !isPrivacyCompliant(consent)) {
        return {
          success: false,
          error: 'Invalid consent preferences'
        };
      }

      const settings: PrivacySettings = {
        consent,
        version: PRIVACY_VERSION,
        last_updated: new Date().toISOString(),
        consentTimestamp: Date.now(),
        ...auditInfo
      };

      // Update database with explicit conflict resolution
      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: userId,
          privacy_settings: settings
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        logger.error('Error updating privacy settings', error, { userId });
        return {
          success: false,
          error: 'Failed to update privacy settings'
        };
      }

      // Update cache
      this.cache.set(userId, { data: settings, timestamp: Date.now() });

      // Log audit trail
      await this.logConsentAudit(userId, consent, 'updated', auditInfo);

      logger.info('Privacy settings updated', { userId, consent });

      return {
        success: true,
        data: settings
      };
    } catch (error) {
      logger.error('Error updating privacy settings', error, { userId });
      return {
        success: false,
        error: 'Failed to update privacy settings'
      };
    }
  }

  // Anonymous user consent management - now uses cookies
  private checkAnonymousConsent(consentType: ConsentType): ConsentCheckResult {
    try {
      // Attempt migration from localStorage to cookies if not done yet
      if (!this.migrationAttempted) {
        this.migrationAttempted = true;
        cookieConsentManager.migrateFromLocalStorage();
      }

      // Check cookie first
      const cookieConsent = cookieConsentManager.getConsentCookie();
      if (cookieConsent) {
        return {
          hasConsent: cookieConsent[consentType] === true,
          consentType,
          lastUpdated: new Date().toISOString() // Cookie doesn't store exact timestamp
        };
      }

      // Fallback to localStorage for backwards compatibility
      const stored = localStorage.getItem(PRIVACY_STORAGE_KEYS.ANONYMOUS_CONSENT);
      if (stored) {
        try {
          const anonymousConsent: AnonymousConsent = JSON.parse(stored);
          
          // Check if consent is still valid (not too old)
          const isExpired = (Date.now() - anonymousConsent.timestamp) > PRIVACY_CACHE_CONFIG.MAX_AGE;
          if (!isExpired) {
            // Migrate to cookie and remove from localStorage
            if (cookieConsentManager.setConsentCookie(anonymousConsent.consent)) {
              localStorage.removeItem(PRIVACY_STORAGE_KEYS.ANONYMOUS_CONSENT);
              logger.info('Migrated consent from localStorage to cookie');
            }
            
            return {
              hasConsent: anonymousConsent.consent[consentType] === true,
              consentType,
              lastUpdated: new Date(anonymousConsent.timestamp).toISOString()
            };
          } else {
            // Remove expired localStorage consent
            localStorage.removeItem(PRIVACY_STORAGE_KEYS.ANONYMOUS_CONSENT);
          }
        } catch (error) {
          logger.warn('Error parsing localStorage consent', error);
          localStorage.removeItem(PRIVACY_STORAGE_KEYS.ANONYMOUS_CONSENT);
        }
      }

      // No consent stored = privacy-first defaults
      return {
        hasConsent: consentType === 'necessary',
        consentType
      };
    } catch (error) {
      logger.warn('Error checking anonymous consent', error);
      return {
        hasConsent: consentType === 'necessary',
        consentType
      };
    }
  }

  // Store anonymous consent - now uses cookies
  setAnonymousConsent(consent: ConsentPreferences): boolean {
    try {
      if (!isValidConsent(consent) || !isPrivacyCompliant(consent)) {
        return false;
      }

      // Set cookie (primary method)
      const cookieSuccess = cookieConsentManager.setConsentCookie(consent);
      
      // Also store in localStorage as fallback for browsers with cookies disabled
      let localStorageSuccess = false;
      try {
        const anonymousConsent: AnonymousConsent = {
          consent,
          timestamp: Date.now(),
          session_id: this.generateSessionId()
        };

        localStorage.setItem(
          PRIVACY_STORAGE_KEYS.ANONYMOUS_CONSENT, 
          JSON.stringify(anonymousConsent)
        );
        localStorageSuccess = true;
      } catch (localStorageError) {
        logger.warn('localStorage not available, using cookie only', localStorageError);
      }

      if (cookieSuccess) {
        logger.debug('Anonymous consent stored in cookie', { consent });
        return true;
      } else if (localStorageSuccess) {
        logger.debug('Anonymous consent stored in localStorage (cookie failed)', { consent });
        return true;
      }

      return false;
    } catch (error) {
      logger.warn('Error storing anonymous consent', error);
      return false;
    }
  }

  // Transfer anonymous consent to authenticated user
  async transferAnonymousConsent(userId: string): Promise<boolean> {
    try {
      let consentToTransfer: ConsentPreferences | null = null;

      // Check cookie first
      consentToTransfer = cookieConsentManager.getConsentCookie();
      
      // Fallback to localStorage
      if (!consentToTransfer) {
        const stored = localStorage.getItem(PRIVACY_STORAGE_KEYS.ANONYMOUS_CONSENT);
        if (stored) {
          try {
            const anonymousConsent: AnonymousConsent = JSON.parse(stored);
            consentToTransfer = anonymousConsent.consent;
          } catch (error) {
            logger.warn('Error parsing localStorage consent for transfer', error);
          }
        }
      }

      if (!consentToTransfer) {
        return false;
      }
      
      // Transfer to user account
      const result = await this.updatePrivacySettings(userId, consentToTransfer);
      
      if (result.success) {
        // Clean up anonymous consent from both sources
        cookieConsentManager.clearConsentCookie();
        localStorage.removeItem(PRIVACY_STORAGE_KEYS.ANONYMOUS_CONSENT);
        
        // Log transfer
        await this.logConsentAudit(userId, consentToTransfer, 'transferred_from_anonymous');
        
        logger.info('Anonymous consent transferred', { userId });
        return true;
      }

      return false;
    } catch (error) {
      logger.error('Error transferring anonymous consent', error, { userId });
      return false;
    }
  }

  // Clear cache for user
  clearCache(userId?: string): void {
    if (userId) {
      this.cache.delete(userId);
    } else {
      this.cache.clear();
    }
  }

  // Check if anonymous user has any stored consent
  hasAnonymousConsent(): boolean {
    return cookieConsentManager.hasConsentCookie() || 
           !!localStorage.getItem(PRIVACY_STORAGE_KEYS.ANONYMOUS_CONSENT);
  }

  // Private helper methods
  private isValidPrivacySettings(settings: any): settings is PrivacySettings {
    return (
      typeof settings === 'object' &&
      settings !== null &&
      isValidConsent(settings.consent) &&
      typeof settings.version === 'string' &&
      typeof settings.last_updated === 'string' &&
      typeof settings.consentTimestamp === 'number'
    );
  }

  private async logConsentAudit(
    userId: string, 
    consent: ConsentPreferences, 
    action: string,
    auditInfo?: { ip_address?: string; user_agent?: string }
  ): Promise<void> {
    try {
      await supabase.from('consent_audit_log').insert({
        user_id: userId,
        consent_preferences: consent,
        consent_version: PRIVACY_VERSION,
        action,
        ip_address: auditInfo?.ip_address,
        user_agent: auditInfo?.user_agent
      });
    } catch (error) {
      logger.warn('Error logging consent audit', error);
    }
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Export singleton instance
export const privacyService = PrivacyService.getInstance(); 